import type { Colaborador } from "@/types";

export interface PeopleResponse {
  data: Colaborador[];
  cachedAt: string;
  source: "fresh" | "cache" | "stale";
}

export interface RefreshResponse {
  data: Colaborador[];
  refreshedAt: string;
}

/**
 * Carrega o HTTP status e o código de erro da API, para que callers possam
 * distinguir 401/UNAUTHENTICATED (token expirado → renovar sessão) de falhas
 * genuínas do backend sem depender de string-match na mensagem.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(`${code}: ${message}`);
    this.name = "ApiError";
  }

  /** true quando a falha é de autenticação (token expirado/inválido). */
  get isAuth(): boolean {
    return this.status === 401 || this.code === "UNAUTHENTICATED";
  }
}

async function call<T>(path: string, accessToken: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const code = (body as { error?: { code?: string } })?.error?.code ?? "UNKNOWN";
    const msg = (body as { error?: { message?: string } })?.error?.message ?? res.statusText;
    throw new ApiError(res.status, code, msg);
  }
  return res.json() as Promise<T>;
}

export function fetchPeople(accessToken: string): Promise<PeopleResponse> {
  return call<PeopleResponse>("/api/people", accessToken);
}

export function refreshPeople(accessToken: string): Promise<RefreshResponse> {
  return call<RefreshResponse>("/api/people/refresh", accessToken, { method: "POST" });
}
